import { useState, useEffect, Suspense, useRef, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Canvas, useFrame } from '@react-three/fiber'
import { useGLTF, useAnimations, OrbitControls } from '@react-three/drei'
import { clone as skeletonClone } from 'three/examples/jsm/utils/SkeletonUtils'
import * as THREE from 'three'
import { updateMeta, updateLink } from '../utils/seo'
import '../styles/App.css'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001'

// Lightweight spinning 3D model for the landing page
function SpinningModel({ url, scale = 1, speed = 0.5 }) {
  const { scene, animations } = useGLTF(url)
  const meshRef = useRef()
  const cloned = useMemo(() => {
    const c = skeletonClone(scene)
    c.traverse((child) => {
      child.frustumCulled = false
      if (child.isMesh && child.material) {
        const mats = Array.isArray(child.material) ? child.material : [child.material]
        mats.forEach((m) => { m.transparent = false; m.depthWrite = true; m.alphaTest = 0.5 })
      }
      if (child.isSkinnedMesh && child.geometry) {
        if (!child.geometry.boundingSphere) child.geometry.boundingSphere = new THREE.Sphere()
        const orig = child.geometry.computeBoundingSphere.bind(child.geometry)
        child.geometry.computeBoundingSphere = function() {
          try { orig() } catch (e) { this.boundingSphere = this.boundingSphere || new THREE.Sphere(); this.boundingSphere.radius = 100 }
        }
      }
    })
    return c
  }, [scene])
  const { actions } = useAnimations(animations, meshRef)

  useEffect(() => {
    // Play first idle or walk animation
    const clipNames = Object.keys(actions)
    const preferred = clipNames.find(n => n.toLowerCase().includes('idle')) || clipNames.find(n => n.toLowerCase().includes('walk')) || clipNames[0]
    if (preferred && actions[preferred]) {
      actions[preferred].reset().play()
    }
  }, [actions])

  useFrame((_, delta) => {
    if (meshRef.current) {
      meshRef.current.rotation.y += delta * speed
    }
  })

  return <primitive ref={meshRef} object={cloned} scale={scale} dispose={null} />
}

function ModelViewer({ url, scale = 1 }) {
  return (
    <div className="model-viewer-container">
      <Canvas
        camera={{ position: [0, 0.3, 3], fov: 40 }}
        gl={{ alpha: true, antialias: true }}
        style={{ background: 'transparent' }}
      >
        <ambientLight intensity={0.8} />
        <directionalLight position={[3, 5, 4]} intensity={1.2} />
        <Suspense fallback={null}>
          <SpinningModel url={url} scale={scale} />
        </Suspense>
      </Canvas>
    </div>
  )
}

function Landing() {
  const [isVisible, setIsVisible] = useState(false)
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    setIsVisible(true)

    // Per-page SEO
    document.title = 'Stream Familiars - 2D & 3D Stream Critters for Twitch | Interactive Chat Overlay'
    updateMeta('description', 'Add interactive stream critters to your Twitch overlay. 35+ critter types with 2D sprites and 3D animated models. Chat commands like !pet, !feed, !dance, !hug, !bonk. Free OBS overlay for streamers.')
    updateMeta('og:title', 'Stream Familiars - 2D & 3D Stream Critters for Twitch', 'property')
    updateMeta('og:description', 'Interactive stream critters for Twitch. 2D sprites and 3D animated models with 14 animation states, 10 chat commands, real-time chat bubbles. Free OBS overlay.', 'property')
    updateMeta('og:url', 'https://streamfamiliars.com/', 'property')
    updateLink('canonical', 'https://streamfamiliars.com/')

    const handleScroll = () => {
      setScrolled(window.scrollY > 20)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const handleLogin = () => {
    window.location.href = `${API_URL}/twitch/auth/login`
  }

  return (
    <div className="landing">
      {/* Navigation */}
      <nav className={`landing-nav ${scrolled ? 'scrolled' : ''}`}>
        <div className="nav-content">
          <div className="nav-logo">
            <div className="logo-mark">SF</div>
            <span className="logo-text">Stream Familiars</span>
          </div>
          <div className="nav-links">
            <a href="#features">Features</a>
            <a href="#setup">Setup</a>
            <Link to="/tutorial">Tutorial</Link>
            <Link to="/guide">Docs</Link>
          </div>
          <button className="nav-cta" onClick={handleLogin}>
            Connect Twitch
          </button>
        </div>
      </nav>

      <main className="landing-main">
        {/* Hero Section */}
        <section className={`hero ${isVisible ? 'visible' : ''}`}>
          <div className="hero-content">
            <div className="hero-text">
              <div className="hero-badge">
                <span className="badge-dot"></span>
                Now with 2D sprites + 3D animated models
              </div>
              <h1 className="hero-title">
                Stream critters,<br />
                <span className="title-accent">alive on Twitch</span>
              </h1>
              <p className="hero-description">
                Give your subscribers animated 2D or 3D companion pets that react to chat in real-time.
                35+ critter types, 10 chat commands, fully customizable OBS overlay.
              </p>
              <div className="hero-actions">
                <button className="btn-primary" onClick={handleLogin}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z"/>
                  </svg>
                  Get Started Free
                </button>
                <a href="#features" className="btn-ghost">
                  See features
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M7 17L17 7M17 7H7M17 7V17"/>
                  </svg>
                </a>
              </div>
            </div>
            <div className="hero-visual">
              <div className="hero-preview">
                <div className="preview-glow"></div>
                <div className="preview-frame">
                  <div className="frame-header">
                    <div className="frame-dots">
                      <span></span><span></span><span></span>
                    </div>
                    <span className="frame-title">overlay / your_channel</span>
                  </div>
                  <div className="frame-content">
                    <div className="demo-critter c1">
                      <div className="critter-bubble">!pet</div>
                      <img src="/assets/cat_mouth_closed.png" alt="2D Cat stream critter" />
                      <span className="critter-label">viewer_123</span>
                    </div>
                    <div className="demo-critter-3d c2">
                      <div className="critter-3d-badge">3D</div>
                      <ModelViewer url="/assets/Dog.glb" scale={1.2} />
                      <span className="critter-label">subscriber</span>
                    </div>
                    <div className="demo-critter c3">
                      <div className="critter-bubble">GG!</div>
                      <img src="/assets/bunny_mouth_closed.png" alt="2D Bunny stream critter" />
                      <span className="critter-label">mod_user</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="hero-stats">
            <div className="stat-item">
              <span className="stat-number">35+</span>
              <span className="stat-text">Critter types</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">2D + 3D</span>
              <span className="stat-text">Sprites & models</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">14</span>
              <span className="stat-text">Animation states</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">Free</span>
              <span className="stat-text">Open source</span>
            </div>
          </div>
        </section>

        {/* 2D + 3D Showcase */}
        <section className="showcase-section">
          <div className="section-intro">
            <span className="section-label">2D & 3D Stream Critters</span>
            <h2>Choose your style</h2>
            <p>Classic 2D pixel-art sprites or fully animated 3D models - mix and match on the same overlay</p>
          </div>

          <div className="showcase-grid">
            <div className="showcase-card showcase-2d">
              <div className="showcase-header">
                <span className="showcase-badge badge-2d">2D Sprites</span>
              </div>
              <h3>Classic 2D Critters</h3>
              <p>Static PNG images with CSS animations. Upload custom artwork or use the 8 built-in types. Support for animated spritesheets with idle, walk, eat, and pet states.</p>
              <div className="showcase-critters">
                <img src="/assets/cat_mouth_closed.png" alt="Cat" className="showcase-img s-float-1" />
                <img src="/assets/fox_mouth_closed.png" alt="Fox" className="showcase-img s-float-2" />
                <img src="/assets/penguin_mouth_closed.png" alt="Penguin" className="showcase-img s-float-3" />
                <img src="/assets/bunny_mouth_closed.png" alt="Bunny" className="showcase-img s-float-4" />
              </div>
              <div className="card-tags">
                <span className="tag">Cat</span>
                <span className="tag">Bunny</span>
                <span className="tag">Owl</span>
                <span className="tag">Fox</span>
                <span className="tag">Penguin</span>
                <span className="tag">Hamster</span>
                <span className="tag">Panda</span>
                <span className="tag">Slime</span>
                <span className="tag tag-plus">+ Custom</span>
              </div>
            </div>

            <div className="showcase-card showcase-3d">
              <div className="showcase-header">
                <span className="showcase-badge badge-3d">3D Models</span>
              </div>
              <h3>Animated 3D Critters</h3>
              <p>Upload GLB models with 14 animation states. Auto-mapped idle cycling, walk, eat, dance, trick, hug, bonk, and more. Export from Unity, Blender, or any 3D tool.</p>
              <div className="showcase-3d-models">
                <ModelViewer url="/assets/Owl.glb" scale={1.1} />
                <ModelViewer url="/assets/Cat.glb" scale={1.1} />
                <ModelViewer url="/assets/Dog.glb" scale={1.1} />
              </div>
              <div className="showcase-3d-grid">
                <span className="tag-3d">Buffalo</span>
                <span className="tag-3d">Cow</span>
                <span className="tag-3d">Dog</span>
                <span className="tag-3d">Cat</span>
                <span className="tag-3d">Fox</span>
                <span className="tag-3d">Wolf</span>
                <span className="tag-3d">Eagle</span>
                <span className="tag-3d">Rabbit</span>
                <span className="tag-3d">Owl</span>
                <span className="tag-3d">+18 more</span>
              </div>
              <div className="card-tags">
                <span className="tag">GLB Upload</span>
                <span className="tag">14 Animations</span>
                <span className="tag">Auto-mapping</span>
                <span className="tag">Unity Export</span>
              </div>
            </div>
          </div>
        </section>

        {/* Features Bento Grid */}
        <section id="features" className="features-section">
          <div className="section-intro">
            <span className="section-label">Features</span>
            <h2>Everything your stream needs</h2>
            <p>Built for Twitch streamers who want interactive chat companions</p>
          </div>

          <div className="bento-grid">
            <div className="bento-card large">
              <div className="card-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                </svg>
              </div>
              <h3>10 Chat Commands</h3>
              <p>
                Viewers interact with their stream critters using chat commands.
                Pet, feed, dance, trick, hug, and bonk - each with unique animations and particle effects.
              </p>
              <div className="card-tags" style={{marginTop: '16px'}}>
                <span className="tag">!pet</span>
                <span className="tag">!feed</span>
                <span className="tag">!dance</span>
                <span className="tag">!trick</span>
                <span className="tag">!hug</span>
                <span className="tag">!bonk</span>
                <span className="tag">!feast</span>
              </div>
            </div>

            <div className="bento-card">
              <div className="card-icon purple">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
              </div>
              <h3>Chat Bubbles</h3>
              <p>Messages appear as speech bubbles above critters in real-time</p>
            </div>

            <div className="bento-card">
              <div className="card-icon blue">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                  <line x1="3" y1="9" x2="21" y2="9"/>
                  <line x1="9" y1="21" x2="9" y2="9"/>
                </svg>
              </div>
              <h3>OBS Ready</h3>
              <p>One URL, paste as browser source in OBS, done in 60 seconds</p>
            </div>

            <div className="bento-card wide">
              <div className="card-icon green">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                  <path d="M2 17l10 5 10-5"/>
                  <path d="M2 12l10 5 10-5"/>
                </svg>
              </div>
              <h3>Split 2D / 3D Settings</h3>
              <p>
                Separate settings panels for 2D sprites and 3D models. Each mode has its own
                admin tools, critter type dropdowns, and configuration. Mix both on the same overlay.
              </p>
              <div className="card-tags">
                <span className="tag" style={{borderLeft: '2px solid #a78bfa'}}>2D Settings</span>
                <span className="tag" style={{borderLeft: '2px solid #10b981'}}>3D Settings</span>
                <span className="tag">Independent Config</span>
              </div>
            </div>

            <div className="bento-card">
              <div className="card-icon orange">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
              </div>
              <h3>Sleep Mode</h3>
              <p>Inactive viewers' critters fall asleep with Zzz - wake when they chat again</p>
            </div>

            <div className="bento-card">
              <div className="card-icon pink">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                </svg>
              </div>
              <h3>Permissions</h3>
              <p>Sub-only, tier gates, mod controls - your rules, your stream</p>
            </div>
          </div>
        </section>

        {/* Setup Section */}
        <section id="setup" className="setup-section">
          <div className="section-intro">
            <span className="section-label">Setup</span>
            <h2>Live in 60 seconds</h2>
            <p>From sign-in to stream critters on your Twitch overlay</p>
          </div>

          <div className="setup-steps">
            <div className="setup-step">
              <div className="step-indicator">
                <span className="step-num">01</span>
                <div className="step-line"></div>
              </div>
              <div className="step-content">
                <h3>Connect Twitch</h3>
                <p>Sign in with your Twitch broadcaster account. One click, instant auth.</p>
              </div>
            </div>

            <div className="setup-step">
              <div className="step-indicator">
                <span className="step-num">02</span>
                <div className="step-line"></div>
              </div>
              <div className="step-content">
                <h3>Configure 2D & 3D</h3>
                <p>Choose critter types, upload custom sprites or 3D GLB models, set subscriber tiers and permissions.</p>
              </div>
            </div>

            <div className="setup-step">
              <div className="step-indicator">
                <span className="step-num">03</span>
              </div>
              <div className="step-content">
                <h3>Go Live</h3>
                <p>Copy your overlay URL, add as OBS browser source. Subscribers type !joincritter to spawn their pet.</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="cta-section">
          <div className="cta-container">
            <div className="cta-content">
              <h2>Ready to add stream critters to your Twitch channel?</h2>
              <p>Free, open source, and fully customizable. 2D sprites and 3D animated models.</p>
              <button className="btn-primary large" onClick={handleLogin}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z"/>
                </svg>
                Start with Twitch
              </button>
            </div>
            <div className="cta-decoration">
              <img src="/assets/hamster_mouth_closed.png" alt="Stream critter" className="float-critter c1" />
              <img src="/assets/slime_mouth_closed.png" alt="Stream critter" className="float-critter c2" />
              <img src="/assets/fox_mouth_closed.png" alt="Stream critter" className="float-critter c3" />
              <img src="/assets/penguin_mouth_closed.png" alt="Stream critter" className="float-critter c4" />
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="footer-content">
          <div className="footer-brand">
            <div className="logo-mark small">SF</div>
            <span>Stream Familiars</span>
          </div>
          <div className="footer-meta">
            <span>2026</span>
            <span className="dot"></span>
            <span>Made for streamers</span>
          </div>
        </div>
      </footer>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

        .landing {
          min-height: 100vh;
          background: #0a0a0b;
          color: #e4e4e7;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          overflow-x: hidden;
        }

        body {
          overflow: auto !important;
        }

        /* Navigation */
        .landing-nav {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 100;
          padding: 16px 24px;
          transition: all 0.3s ease;
        }

        .landing-nav.scrolled {
          background: rgba(10, 10, 11, 0.8);
          backdrop-filter: blur(20px);
          border-bottom: 1px solid rgba(255,255,255,0.06);
        }

        .nav-content {
          max-width: 1200px;
          margin: 0 auto;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .nav-logo {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .logo-mark {
          width: 36px;
          height: 36px;
          background: #a78bfa;
          color: #0a0a0b;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 14px;
        }

        .logo-mark.small {
          width: 28px;
          height: 28px;
          font-size: 11px;
          border-radius: 8px;
        }

        .logo-text {
          font-weight: 600;
          font-size: 15px;
          color: #fafafa;
        }

        .nav-links {
          display: flex;
          gap: 32px;
        }

        .nav-links a {
          color: #71717a;
          text-decoration: none;
          font-size: 14px;
          font-weight: 500;
          transition: color 0.2s;
        }

        .nav-links a:hover {
          color: #fafafa;
        }

        .nav-cta {
          background: rgba(255,255,255,0.08);
          color: #fafafa;
          border: 1px solid rgba(255,255,255,0.1);
          padding: 10px 20px;
          border-radius: 10px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .nav-cta:hover {
          background: rgba(255,255,255,0.12);
          border-color: rgba(255,255,255,0.15);
        }

        /* Hero Section */
        .landing-main {
          padding-top: 100px;
        }

        .hero {
          max-width: 1200px;
          margin: 0 auto;
          padding: 60px 24px;
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .hero.visible {
          opacity: 1;
          transform: translateY(0);
        }

        .hero-content {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 60px;
          align-items: center;
          margin-bottom: 80px;
        }

        .hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: rgba(167, 139, 250, 0.1);
          color: #a78bfa;
          padding: 8px 16px;
          border-radius: 100px;
          font-size: 13px;
          font-weight: 500;
          margin-bottom: 24px;
        }

        .badge-dot {
          width: 6px;
          height: 6px;
          background: #a78bfa;
          border-radius: 50%;
          animation: pulse 2s infinite;
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }

        .hero-title {
          font-size: clamp(40px, 5vw, 64px);
          font-weight: 800;
          line-height: 1.1;
          letter-spacing: -0.03em;
          color: #fafafa;
          margin-bottom: 24px;
        }

        .title-accent {
          color: #a78bfa;
        }

        .hero-description {
          font-size: 18px;
          line-height: 1.7;
          color: #71717a;
          margin-bottom: 40px;
          max-width: 480px;
        }

        .hero-actions {
          display: flex;
          gap: 16px;
          align-items: center;
        }

        .btn-primary {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          background: #a78bfa;
          color: #0a0a0b;
          border: none;
          padding: 14px 28px;
          border-radius: 12px;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-primary:hover {
          background: #c4b5fd;
          transform: translateY(-2px);
        }

        .btn-primary.large {
          padding: 18px 36px;
          font-size: 16px;
        }

        .btn-ghost {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          color: #71717a;
          text-decoration: none;
          font-size: 15px;
          font-weight: 500;
          padding: 14px 20px;
          transition: all 0.2s;
        }

        .btn-ghost:hover {
          color: #fafafa;
        }

        /* Hero Visual */
        .hero-visual {
          position: relative;
        }

        .hero-preview {
          position: relative;
        }

        .preview-glow {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 300px;
          height: 300px;
          background: rgba(167, 139, 250, 0.15);
          filter: blur(100px);
          border-radius: 50%;
          pointer-events: none;
        }

        .preview-frame {
          position: relative;
          background: #141416;
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 16px;
          overflow: hidden;
        }

        .frame-header {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px 18px;
          background: rgba(255,255,255,0.02);
          border-bottom: 1px solid rgba(255,255,255,0.06);
        }

        .frame-dots {
          display: flex;
          gap: 6px;
        }

        .frame-dots span {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          background: #27272a;
        }

        .frame-dots span:first-child { background: #ef4444; }
        .frame-dots span:nth-child(2) { background: #eab308; }
        .frame-dots span:last-child { background: #22c55e; }

        .frame-title {
          font-size: 12px;
          color: #52525b;
          font-family: monospace;
        }

        .frame-content {
          padding: 40px 30px;
          min-height: 220px;
          display: flex;
          align-items: flex-end;
          justify-content: center;
          gap: 40px;
          background: #0c0c0d;
        }

        .demo-critter {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          animation: float 3s ease-in-out infinite;
        }

        .demo-critter.c2 { animation-delay: 0.5s; }
        .demo-critter.c3 { animation-delay: 1s; }

        .demo-critter img {
          width: 70px;
          height: 70px;
          object-fit: contain;
        }

        .critter-bubble {
          background: #fafafa;
          color: #0a0a0b;
          padding: 8px 14px;
          border-radius: 12px;
          font-size: 13px;
          font-weight: 500;
          position: relative;
        }

        .critter-bubble::after {
          content: '';
          position: absolute;
          bottom: -5px;
          left: 50%;
          transform: translateX(-50%);
          border-left: 5px solid transparent;
          border-right: 5px solid transparent;
          border-top: 5px solid #fafafa;
        }

        .critter-label {
          font-size: 11px;
          color: #52525b;
          font-family: monospace;
        }

        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }

        /* Hero Stats */
        .hero-stats {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 24px;
          padding: 32px;
          background: rgba(255,255,255,0.02);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 16px;
        }

        .stat-item {
          text-align: center;
        }

        .stat-number {
          display: block;
          font-size: 24px;
          font-weight: 700;
          color: #fafafa;
          margin-bottom: 4px;
        }

        .stat-text {
          font-size: 13px;
          color: #52525b;
        }

        /* 3D Badge in hero preview */
        .critter-3d-badge {
          background: #10b981;
          color: #0a0a0b;
          font-size: 10px;
          font-weight: 800;
          padding: 3px 8px;
          border-radius: 4px;
          letter-spacing: 0.05em;
        }

        /* Showcase Section */
        .showcase-section {
          max-width: 1200px;
          margin: 0 auto;
          padding: 80px 24px 40px;
        }

        .showcase-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 24px;
        }

        .showcase-card {
          background: #141416;
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 20px;
          padding: 32px;
          transition: all 0.3s;
        }

        .showcase-card:hover {
          border-color: rgba(255,255,255,0.12);
          transform: translateY(-4px);
        }

        .showcase-2d { border-top: 3px solid #a78bfa; }
        .showcase-3d { border-top: 3px solid #10b981; }

        .showcase-header {
          margin-bottom: 16px;
        }

        .showcase-badge {
          display: inline-block;
          padding: 5px 14px;
          border-radius: 100px;
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 0.04em;
        }

        .badge-2d {
          background: rgba(167, 139, 250, 0.12);
          color: #a78bfa;
        }

        .badge-3d {
          background: rgba(16, 185, 129, 0.12);
          color: #10b981;
        }

        .showcase-card h3 {
          font-size: 22px;
          font-weight: 700;
          color: #fafafa;
          margin-bottom: 10px;
        }

        .showcase-card > p {
          font-size: 14px;
          color: #71717a;
          line-height: 1.7;
          margin-bottom: 20px;
        }

        .showcase-critters {
          display: flex;
          gap: 12px;
          justify-content: center;
          margin-bottom: 20px;
          padding: 20px 0;
        }

        .showcase-img {
          width: 56px;
          height: 56px;
          object-fit: contain;
        }

        /* 3D Model Viewer */
        .model-viewer-container {
          width: 90px;
          height: 90px;
          border-radius: 12px;
          overflow: hidden;
        }

        .demo-critter-3d {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
          animation: float 3s ease-in-out infinite;
        }

        .demo-critter-3d .model-viewer-container {
          width: 80px;
          height: 80px;
        }

        .showcase-3d-models {
          display: flex;
          gap: 16px;
          justify-content: center;
          margin: 16px 0 20px;
        }

        .showcase-3d-models .model-viewer-container {
          width: 100px;
          height: 100px;
          background: rgba(16, 185, 129, 0.05);
          border: 1px solid rgba(16, 185, 129, 0.1);
          border-radius: 14px;
        }

        .s-float-1 { animation: float 3s ease-in-out infinite; }
        .s-float-2 { animation: float 3s ease-in-out infinite 0.4s; }
        .s-float-3 { animation: float 3s ease-in-out infinite 0.8s; }
        .s-float-4 { animation: float 3s ease-in-out infinite 1.2s; }

        .showcase-3d-grid {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          margin-bottom: 20px;
        }

        .tag-3d {
          background: rgba(16, 185, 129, 0.08);
          color: #10b981;
          padding: 4px 10px;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 600;
        }

        .tag-plus {
          background: rgba(167, 139, 250, 0.1);
          color: #a78bfa;
        }

        /* Features Bento Grid */
        .features-section {
          max-width: 1200px;
          margin: 0 auto;
          padding: 100px 24px;
        }

        .section-intro {
          text-align: center;
          margin-bottom: 60px;
        }

        .section-label {
          display: inline-block;
          font-size: 13px;
          font-weight: 600;
          color: #a78bfa;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          margin-bottom: 16px;
        }

        .section-intro h2 {
          font-size: clamp(32px, 4vw, 48px);
          font-weight: 700;
          color: #fafafa;
          margin-bottom: 16px;
          letter-spacing: -0.02em;
        }

        .section-intro p {
          font-size: 18px;
          color: #71717a;
        }

        .bento-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
        }

        .bento-card {
          background: #141416;
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 20px;
          padding: 28px;
          transition: all 0.3s ease;
        }

        .bento-card:hover {
          border-color: rgba(255,255,255,0.12);
          transform: translateY(-4px);
        }

        .bento-card.large {
          grid-column: span 1;
          grid-row: span 2;
        }

        .bento-card.wide {
          grid-column: span 2;
        }

        .card-icon {
          width: 44px;
          height: 44px;
          background: rgba(167, 139, 250, 0.1);
          color: #a78bfa;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 20px;
        }

        .card-icon.purple { background: rgba(167, 139, 250, 0.1); color: #a78bfa; }
        .card-icon.blue { background: rgba(59, 130, 246, 0.1); color: #3b82f6; }
        .card-icon.green { background: rgba(34, 197, 94, 0.1); color: #22c55e; }
        .card-icon.orange { background: rgba(249, 115, 22, 0.1); color: #f97316; }
        .card-icon.pink { background: rgba(236, 72, 153, 0.1); color: #ec4899; }

        .bento-card h3 {
          font-size: 18px;
          font-weight: 600;
          color: #fafafa;
          margin-bottom: 10px;
        }

        .bento-card p {
          font-size: 14px;
          color: #71717a;
          line-height: 1.6;
        }

        .card-visual {
          display: flex;
          gap: 16px;
          margin-top: 24px;
          padding-top: 24px;
          border-top: 1px solid rgba(255,255,255,0.06);
        }

        .mini-critter img {
          width: 50px;
          height: 50px;
          object-fit: contain;
          opacity: 0.8;
        }

        .card-tags {
          display: flex;
          gap: 8px;
          margin-top: 20px;
          flex-wrap: wrap;
        }

        .tag {
          background: rgba(255,255,255,0.05);
          color: #a1a1aa;
          padding: 6px 12px;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 500;
        }

        /* Setup Section */
        .setup-section {
          background: #0c0c0d;
          padding: 100px 24px;
        }

        .setup-steps {
          max-width: 600px;
          margin: 0 auto;
        }

        .setup-step {
          display: flex;
          gap: 24px;
          padding-bottom: 48px;
        }

        .setup-step:last-child {
          padding-bottom: 0;
        }

        .step-indicator {
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .step-num {
          width: 48px;
          height: 48px;
          background: rgba(167, 139, 250, 0.1);
          color: #a78bfa;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          font-weight: 700;
          flex-shrink: 0;
        }

        .step-line {
          width: 2px;
          flex: 1;
          background: rgba(255,255,255,0.06);
          margin-top: 16px;
        }

        .step-content h3 {
          font-size: 20px;
          font-weight: 600;
          color: #fafafa;
          margin-bottom: 8px;
        }

        .step-content p {
          font-size: 15px;
          color: #71717a;
          line-height: 1.6;
        }

        /* CTA Section */
        .cta-section {
          padding: 100px 24px;
        }

        .cta-container {
          max-width: 800px;
          margin: 0 auto;
          background: #141416;
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 24px;
          padding: 60px;
          position: relative;
          overflow: hidden;
        }

        .cta-content {
          position: relative;
          z-index: 1;
          text-align: center;
        }

        .cta-content h2 {
          font-size: clamp(28px, 4vw, 40px);
          font-weight: 700;
          color: #fafafa;
          margin-bottom: 12px;
          letter-spacing: -0.02em;
        }

        .cta-content p {
          font-size: 16px;
          color: #71717a;
          margin-bottom: 32px;
        }

        .cta-decoration {
          position: absolute;
          inset: 0;
          pointer-events: none;
        }

        .float-critter {
          position: absolute;
          width: 60px;
          height: 60px;
          object-fit: contain;
          opacity: 0.3;
        }

        .float-critter.c1 {
          top: 20px;
          right: 40px;
          animation: floatSlow 4s ease-in-out infinite;
        }

        .float-critter.c2 {
          bottom: 20px;
          left: 40px;
          animation: floatSlow 4s ease-in-out infinite 1s;
        }

        .float-critter.c3 {
          top: 50%;
          left: 30px;
          animation: floatSlow 4s ease-in-out infinite 0.5s;
          width: 45px;
          height: 45px;
          opacity: 0.2;
        }

        .float-critter.c4 {
          bottom: 40px;
          right: 30px;
          animation: floatSlow 4s ease-in-out infinite 1.5s;
          width: 45px;
          height: 45px;
          opacity: 0.2;
        }

        @keyframes floatSlow {
          0%, 100% { transform: translateY(0) rotate(-5deg); }
          50% { transform: translateY(-15px) rotate(5deg); }
        }

        /* Footer */
        .landing-footer {
          border-top: 1px solid rgba(255,255,255,0.06);
          padding: 24px;
        }

        .footer-content {
          max-width: 1200px;
          margin: 0 auto;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .footer-brand {
          display: flex;
          align-items: center;
          gap: 10px;
          color: #71717a;
          font-size: 14px;
        }

        .footer-meta {
          display: flex;
          align-items: center;
          gap: 12px;
          color: #52525b;
          font-size: 13px;
        }

        .footer-meta .dot {
          width: 3px;
          height: 3px;
          background: #3f3f46;
          border-radius: 50%;
        }

        /* Mobile Responsiveness */
        @media (max-width: 968px) {
          .hero-content {
            grid-template-columns: 1fr;
            gap: 48px;
            text-align: center;
          }

          .hero-description {
            margin-left: auto;
            margin-right: auto;
          }

          .hero-actions {
            justify-content: center;
          }

          .hero-stats {
            grid-template-columns: repeat(2, 1fr);
          }

          .showcase-grid {
            grid-template-columns: 1fr;
          }

          .bento-grid {
            grid-template-columns: 1fr;
          }

          .bento-card.large,
          .bento-card.wide {
            grid-column: span 1;
            grid-row: span 1;
          }
        }

        @media (max-width: 640px) {
          .landing-nav {
            padding: 12px 16px;
          }

          .nav-links {
            display: none;
          }

          .hero {
            padding: 40px 16px;
          }

          .hero-stats {
            padding: 24px;
            gap: 16px;
          }

          .stat-number {
            font-size: 20px;
          }

          .frame-content {
            gap: 24px;
            padding: 30px 20px;
          }

          .demo-critter img {
            width: 50px;
            height: 50px;
          }

          .cta-container {
            padding: 40px 24px;
          }

          .footer-content {
            flex-direction: column;
            gap: 16px;
            text-align: center;
          }
        }
      `}</style>
    </div>
  )
}

export default Landing
